import React, { useMemo } from 'react';
import { Grid, GridItem } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { EventQueueWireDto } from '@mark43/cad-api';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { WorkspaceQueue } from '../panels/workspace-queue';
import { FrAssignButton } from '../fr-event-common-components/fr-assign-button';
import { getEventDataForSelfAssignmentModal } from '../fr-event-common-components/helpers';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { FrCompactQueueCell } from './fr-compact-cells/fr-compact-queue-cell';
import { DataPrivacy } from '../common/components/datadog-privacy';
import { FR_EVENT_DETAILS_TABS } from '../fr-event-details-page/constants';
import { useOpenFrEventDetails } from '../fr-event-details-page/hooks/use-open-fr-event-details';
import {
    DEFAULT_COL_MIN_HEIGHT,
    DEFAULT_AS_COL_WIDTH,
} from '../common/components/fr-queues/constants';

export const FrCompactQueueRow = observer(function FrQueueRow({
    data,
    assignedEvent,
    index,
    queueColumns,
}: {
    data: EventQueueWireDto;
    assignedEvent?: EventQueueWireDto;
    queueColumns: WorkspaceQueue['queueColumns'];
    index: number;
}): React.ReactElement {
    const unitAssignment = useUnitAssignment();
    const { assignSelfToEvents } = useEnabledFeatures();
    const hasAssignedUnits = !!data.assignedUnits?.length;
    const selectedEvent = getEventDataForSelfAssignmentModal({ ...data, eventId: data.id });
    const modifiedAssignedEvent = getEventDataForSelfAssignmentModal({
        ...assignedEvent,
        eventId: assignedEvent?.id,
    });
    const isUnitAssignedToCurrentEvent = useMemo(
        () => (data.assignedUnits || []).some((unit) => unit.id === unitAssignment?.unitId),
        [data.assignedUnits, unitAssignment?.unitId]
    );
    const showAssignButton =
        !!unitAssignment && assignSelfToEvents && !isUnitAssignedToCurrentEvent;

    const openFrEventDetails = useOpenFrEventDetails();
    const handleOpenEventNarrativeTab = () => {
        if (!data.id) return;
        openFrEventDetails(data.id, FR_EVENT_DETAILS_TABS.NARRATIVE);
    };

    return (
        <Grid
            position="relative"
            data-testid={`queue-row-${data.id}`}
            data-row-index={index}
            borderBottom="1px solid"
            borderColor="border.default"
            gridTemplateColumns={`var(--columns) ${DEFAULT_AS_COL_WIDTH}px`}
            onClick={handleOpenEventNarrativeTab}
            _hover={{
                cursor: 'pointer',
                backgroundColor: 'var(--arc-colors-interactive-hover)',
            }}
        >
            {queueColumns.map((col) => (
                <GridItem key={col.id}>
                    <FrCompactQueueCell col={col} data={data} />
                </GridItem>
            ))}
            <GridItem
                minHeight={`${DEFAULT_COL_MIN_HEIGHT}px`}
                alignItems="center"
                alignContent="center"
                marginLeft="auto"
            >
                {showAssignButton && (
                    <DataPrivacy privacy="unmask">
                        <FrAssignButton
                            size="xl"
                            hasAssignedUnits={hasAssignedUnits}
                            selectedEvent={selectedEvent}
                            assignedEvent={modifiedAssignedEvent}
                            style={{ minWidth: 140 }}
                        />
                    </DataPrivacy>
                )}
            </GridItem>
        </Grid>
    );
});
